// src/features/sqlite/useSQLiteDatabase.ts
import { useState, useEffect } from 'react';
import initSqlJs, { Database } from 'sql.js';

interface SQLiteState {
  db: Database | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to load a remote SQLite file (via URL) into sql.js in‐memory database.
 * Intended for “feature: import SQLite file,” not the primary DuckDB engine.
 */
export function useSQLiteDatabase(sqliteUrl: string): SQLiteState {
  const [db, setDb] = useState<Database | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function loadDB() {
      try {
        // Initialize sql.js
        const SQL = await initSqlJs({
          locateFile: (file: string) => `/sql-wasm.wasm`,
        });
        // Download the SQLite file
        const response = await fetch(sqliteUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch SQLite file: ${response.statusText}`);
        }
        const buffer = await response.arrayBuffer();
        // Create a sql.js in‐memory Database
        const database = new SQL.Database(new Uint8Array(buffer));
        setDb(database);
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }

    loadDB();
  }, [sqliteUrl]);

  return { db, loading, error };
}
